import React from 'react';
import { cn } from '@/util/cn';

// Production stat-rendering primitive. Phase 1c of the redesign refactor
// extends the original (icon + title + metric in a horizontal row) with
// two additional visual variants — `row` and `block` — so the redesign
// can land its new stat treatments without forking a parallel component
// from the sandbox.
//
//   variant="default" (omitted) ……… the original look. Icon + title +
//                                   right-aligned metric, fixed sizing.
//                                   All 36 existing callers stay here.
//
//   variant="row" ………………………………………… icon + label + right-aligned value
//                                   with a hairline bottom border. Use
//                                   inside a list of stats (Lifts,
//                                   Trails, Acres, etc.) on the new
//                                   resort surfaces.
//
//   variant="block" …………………………………… eyebrow + big tabular-nums value
//                                   stacked vertically. Use for hero
//                                   stat tiles. `size`, `unit`, `tone`,
//                                   and `secondaryLabel` are
//                                   block-specific extras.

export type MetricListItemVariant = 'default' | 'row' | 'block';
export type MetricListItemSize = 'sm' | 'md' | 'lg';
export type MetricListItemTone = 'default' | 'primary';

export interface IProps {
  title: string,
  metric: string | React.ReactNode,
  className?: string,

  /** Visual variant. Omit (or 'default') for the original look — kept
   *  backward-compatible for the 36 existing call sites. */
  variant?: MetricListItemVariant,

  /** Legacy icon, a React component. Used by the default variant. */
  Icon?: React.FC<{ className?: string }>,

  /** Modern icon, a pre-rendered node. Used by the `row` variant. */
  icon?: React.ReactNode,

  /** `block` variant: trailing unit shown small + muted next to value. */
  unit?: string,
  /** `block` variant: right-aligned secondary text in the label row. */
  secondaryLabel?: string,
  /** `block` variant: colors the numeric value. */
  tone?: MetricListItemTone,
  /** `block` variant: visual scale of the numeric value. */
  size?: MetricListItemSize,

  /** `row` variant: render as <li> instead of <div>. */
  as?: 'div' | 'li',
  /** `row` variant: override how the value is styled. */
  valueClassName?: string,
}

// ─── default variant (legacy) ────────────────────────────────────────────

const boxClass = [
  'flex items-center border-b border-border',
  'py-3 pr-5 mx-[22px]',
  'lg:mx-0',
  'max-md:mx-0 max-md:pr-1',
].join(' ');
const iconClass = 'pr-5 pl-1.5 [&_svg]:h-5';
const titleClass = 'text-xl font-normal max-md:text-[1.1rem]';
const metricClass = 'text-xl ml-auto max-md:text-[1.1rem]';

const DefaultVariant = ({
  title, metric, Icon, className,
}: IProps) => (
  <div className={cn(boxClass, className)}>
    {Icon && (
      <div className={iconClass}>
        <Icon />
      </div>
    )}
    <div className={titleClass}>{title}</div>
    <div className={metricClass}>{metric}</div>
  </div>
);

// ─── row variant ─────────────────────────────────────────────────────────

const RowVariant = ({
  title, metric, icon, Icon, as = 'div', valueClassName, className,
}: IProps) => {
  const Wrapper = as;
  // Accept either the new `icon` ReactNode prop or the legacy `Icon`
  // React.FC. Lets existing call-sites flip to variant="row" by just
  // adding the prop, without rewriting their icon usage.
  const iconNode = icon ?? (Icon ? <Icon /> : null);
  return (
    <Wrapper
      className={cn(
        'flex items-center justify-between gap-4 border-b border-border py-3 last:border-b-0',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        {iconNode && (
          <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary [&_svg]:h-full [&_svg]:w-full [&_svg]:fill-current">
            {iconNode}
          </span>
        )}
        <span className="text-[15px] font-medium">{title}</span>
      </div>
      <span
        className={cn(
          'font-heading text-base font-medium tabular-nums text-foreground',
          valueClassName,
        )}
      >
        {metric}
      </span>
    </Wrapper>
  );
};

// ─── block variant ───────────────────────────────────────────────────────

const BLOCK_SIZE_CLASSES: Record<MetricListItemSize, string> = {
  sm: 'text-xl sm:text-2xl',
  md: 'text-2xl sm:text-3xl',
  lg: 'text-4xl sm:text-5xl',
};

const BlockVariant = ({
  title,
  metric,
  unit,
  secondaryLabel,
  tone = 'default',
  size = 'md',
  className,
}: IProps) => (
  <div className={className}>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </span>
      {secondaryLabel && (
        <span className="text-[10px] font-semibold uppercase tracking-wider tabular-nums text-muted-foreground/80">
          {secondaryLabel}
        </span>
      )}
    </div>
    <div
      className={cn(
        'mt-1 inline-flex items-baseline gap-1.5 font-heading font-bold leading-none tabular-nums',
        BLOCK_SIZE_CLASSES[size],
        tone === 'primary' && 'text-primary',
      )}
    >
      <span>{metric}</span>
      {unit && (
        <span
          className={cn(
            'text-sm font-medium',
            tone === 'primary' ? 'text-primary/70' : 'text-muted-foreground',
          )}
        >
          {unit}
        </span>
      )}
    </div>
  </div>
);

// ─── public component ────────────────────────────────────────────────────

export const MetricListItem = ({ variant = 'default', ...rest }: IProps) => {
  if (variant === 'row') return <RowVariant {...rest} />;
  if (variant === 'block') return <BlockVariant {...rest} />;
  return <DefaultVariant {...rest} />;
};
